// Search's results as a file: the page of rows on screen, with the fields
// the user picked as plain values. The table's cell renderers draw chips;
// here each column is a function from the row to a cell.

import { SearchFilters } from "../../services/searchApi";
import {
  EntityTypePlural,
  getEntityTypeByPlural,
} from "../../types/entityRegistry";
import { getDisplayNamesFromDbValues as facilityStatusNames } from "../../utils/facilityStatusMapping";
import { getDisplayNamesFromDbValues as facilityTypeNames } from "../../utils/facilityTypeMapping";
import {
  ExportField,
  ExportFieldColumn,
  columnField,
  tableOfFields,
} from "../../utils/export/fields";
import { pageNote } from "../../utils/export/pages";
import {
  ExportTable,
  TableFormat,
  writeTableExport,
} from "../../utils/export/table";
import { getLabelDisplay } from "../../utils/labelMapping";
import { getDisplayNamesFromDbValues as orgCategoryNames } from "../../utils/organizationCategoryMapping";
import { getDisplayNameFromDbValue as orgStatusName } from "../../utils/organizationStatusMapping";
import { projectStatusMapping } from "../../utils/projectStatusMapping";
import { projectTypeMapping } from "../../utils/projectTypeMapping";

// What the About sheet names as the source of the file
const SOURCE = "Market Intelligence · Search";

// A result as the search returns it
export type SearchRow = Record<string, unknown>;

// A field as a list of texts: one value or several, the empty left out
const listOf = (value: unknown): string[] =>
  (Array.isArray(value) ? value : [value])
    .filter((item) => item !== null && item !== undefined && item !== "")
    .map(String);

const textOf = (value: unknown): string => listOf(value).join(", ");

const numberOf = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

// A day as the search gives it, as a date; text that is no day stays text
const dayOf = (value: unknown): Date | string | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const day = new Date(String(value));
  return Number.isNaN(day.getTime()) ? String(value) : day;
};

// A name through a mapping that throws on a value it does not know
const named = (name: (value: string) => string) => (value: string) => {
  try {
    return name(value);
  } catch {
    return value;
  }
};

// The address of an entity's page
const pageOf = (row: SearchRow): string | null =>
  row.id ? `${window.location.origin}/entity/${encodeURIComponent(String(row.id))}` : null;

const NAME_WIDTH = 36;

// A field of the catalog: its place in the picker and the column it writes
const field = (
  group: string,
  key: string,
  label: string,
  selected: boolean,
  column: Omit<ExportFieldColumn<SearchRow>, "header">,
): ExportField<SearchRow> =>
  columnField<SearchRow>({ key, label, group, selected }, column);

// The fields that read a result's own key the same way on every type
const textField = (
  group: string,
  key: string,
  label: string,
  selected: boolean,
  width?: number,
): ExportField<SearchRow> =>
  field(group, key, label, selected, { width, value: (row) => textOf(row[key]) });

const listField = (
  group: string,
  key: string,
  label: string,
  selected: boolean,
  width?: number,
): ExportField<SearchRow> =>
  field(group, key, label, selected, { width, value: (row) => listOf(row[key]) });

const numberField = (
  group: string,
  key: string,
  label: string,
  selected: boolean,
): ExportField<SearchRow> =>
  field(group, key, label, selected, {
    kind: "number",
    value: (row) => numberOf(row[key]),
  });

const dateField = (
  group: string,
  key: string,
  label: string,
  selected: boolean,
): ExportField<SearchRow> =>
  field(group, key, label, selected, {
    kind: "date",
    value: (row) => dayOf(row[key]),
  });

const nameField = (): ExportField<SearchRow> =>
  textField("Identity", "name", "Name", true, NAME_WIDTH);

const nativeNameField = (): ExportField<SearchRow> =>
  textField("Identity", "name_native", "Native name", false, NAME_WIDTH);

const otherNamesField = (): ExportField<SearchRow> =>
  textField("Identity", "alternate_names", "Other names", false, NAME_WIDTH);

const descriptionField = (): ExportField<SearchRow> =>
  textField("Identity", "description", "Description", true, 60);

const pageField = (): ExportField<SearchRow> =>
  field("Identity", "page", "Page link", true, {
    kind: "url",
    width: 40,
    value: pageOf,
  });

const idField = (): ExportField<SearchRow> =>
  textField("Identity", "id", "ID", false, 30);

const labelsField = (group: string, selected: boolean): ExportField<SearchRow> =>
  field(group, "labels", "Labels", selected, {
    width: 32,
    value: (row) => listOf(row.labels).map((label) => getLabelDisplay(label).full),
  });

const ORGANIZATION_FIELDS: ExportField<SearchRow>[] = [
  nameField(),
  nativeNameField(),
  otherNamesField(),
  field("Identity", "organization_type", "Type", true, {
    width: 28,
    value: (row) => orgCategoryNames(listOf(row.organization_type)),
  }),
  descriptionField(),
  listField("Identity", "websites", "Websites", true, 40),
  pageField(),
  idField(),
  listField("Location & history", "home_country", "Country", true),
  numberField("Location & history", "founding_year", "Founded", true),
  numberField("Location & history", "number_of_employees", "Employees", true),
  field("Status", "status_labels", "Status", true, {
    width: 24,
    value: (row) => listOf(row.status_labels).map(named(orgStatusName)),
  }),
  textField("Status", "status_summary", "Status summary", false, 48),
  dateField("Status", "status_last_update_date", "Status updated", false),
  listField("Activity", "products", "Products", true, 40),
  listField("Activity", "services", "Services", true, 40),
  listField("Activity", "naics_codes_with_title", "Industry (NAICS)", true, 40),
  listField("Activity", "input_hs_codes_with_title", "Input HS codes", false, 40),
  listField("Activity", "output_hs_codes_with_title", "Output HS codes", false, 40),
  labelsField("Activity", true),
  numberField("Record", "n_sources", "Number of sources", false),
];

const FACILITY_FIELDS: ExportField<SearchRow>[] = [
  nameField(),
  nativeNameField(),
  otherNamesField(),
  field("Identity", "facility_type", "Type", true, {
    width: 28,
    value: (row) => facilityTypeNames(listOf(row.facility_type)),
  }),
  descriptionField(),
  pageField(),
  idField(),
  listField("Location", "country_iso2", "Country", true),
  textField("Location", "address", "Address", true, 40),
  numberField("Location", "latitude", "Latitude", false),
  numberField("Location", "longitude", "Longitude", false),
  field("Status", "status_labels", "Status", true, {
    width: 24,
    value: (row) => facilityStatusNames(listOf(row.status_labels)),
  }),
  textField("Status", "operational_from", "Operational from", true),
  textField("Status", "status_summary", "Status summary", false, 48),
  dateField("Status", "status_last_update_date", "Status updated", false),
  numberField("Activity", "number_of_employees", "Employees", true),
  listField("Activity", "products_produced", "Products produced", true, 40),
  listField("Activity", "services", "Services", false, 40),
  listField("Activity", "technologies_used", "Technologies used", false, 40),
  listField("Activity", "product_use_cases", "Product use cases", false, 40),
  listField("Activity", "naics_codes_with_title", "Industry (NAICS)", false, 40),
  listField("Activity", "input_hs_codes_with_title", "Input HS codes", false, 40),
  listField("Activity", "output_hs_codes_with_title", "Output HS codes", false, 40),
  labelsField("Activity", false),
  numberField("Record", "n_sources", "Number of sources", false),
];

const PROJECT_FIELDS: ExportField<SearchRow>[] = [
  nameField(),
  field("Identity", "project_type", "Type", true, {
    width: 28,
    value: (row) =>
      listOf(row.project_type).map(named(projectTypeMapping.getDisplayName)),
  }),
  descriptionField(),
  listField("Identity", "keywords", "Keywords", false, 40),
  listField("Identity", "naics_codes_with_title", "Industry (NAICS)", false, 40),
  pageField(),
  idField(),
  field("Status & timing", "project_status", "Status", true, {
    width: 24,
    value: (row) =>
      listOf(row.project_status).map((id) => projectStatusMapping.getDisplayName(id)),
  }),
  listField("Status & timing", "project_countries", "Countries", true),
  // Free text as recorded, often a bare year: a date would invent a day
  textField("Status & timing", "project_start", "Start", true),
  textField("Status & timing", "project_end", "End", true),
  dateField("Status & timing", "publication_date", "Last updated", true),
  numberField("Budget", "project_volume_value", "Budget", true),
  textField("Budget", "project_volume_currency", "Currency", true),
  textField("Budget", "project_volume_text", "Budget as stated", false, 32),
  numberField("Budget", "project_volume_min", "Budget min", false),
  numberField("Budget", "project_volume_max", "Budget max", false),
  textField("Budget", "round_label", "Funding round", false),
];

const SOURCE_FIELDS: ExportField<SearchRow>[] = [
  textField("Article", "title", "Title", true, 48),
  dateField("Article", "publication_date", "Published", true),
  textField("Article", "domain", "Source site", true),
  field("Article", "url", "URL", true, {
    kind: "url",
    width: 40,
    value: (row) => textOf(row.url),
  }),
  textField("Article", "language", "Language", true),
  listField("Article", "authors", "Authors", true, 28),
  textField("Article", "summary", "Summary", true, 60),
  textField("Article", "id", "ID", false, 30),
];

// The fields offered for each searchable type, in the order of the picker
export const SEARCH_EXPORT_FIELDS: Record<EntityTypePlural, ExportField<SearchRow>[]> = {
  organizations: ORGANIZATION_FIELDS,
  facilities: FACILITY_FIELDS,
  projects: PROJECT_FIELDS,
  sources: SOURCE_FIELDS,
};

const typeLabel = (entityType: EntityTypePlural): string =>
  getEntityTypeByPlural(entityType)?.pluralLabel ?? entityType;

// The rows of one type as a table of the fields picked
export const tableOfSearchResults = (
  entityType: EntityTypePlural,
  keys: readonly string[],
  rows: readonly SearchRow[],
): ExportTable =>
  tableOfFields(typeLabel(entityType), SEARCH_EXPORT_FIELDS[entityType], keys, rows);

// The request fields that are no filter
const NOT_A_FILTER = new Set(["q", "entity_type", "limit", "offset", "sort", "order"]);

const humanise = (key: string): string => {
  const words = key.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
};

// The filters of a request as label and value pairs: the keyword, each
// filter in force, and the order
export const searchExportFilters = (filters: SearchFilters): [string, string][] => {
  const pairs: [string, string][] = [];
  if (filters.q) {
    pairs.push(["Keyword", filters.q]);
  }
  for (const [key, value] of Object.entries(filters)) {
    if (NOT_A_FILTER.has(key) || value === undefined || value === null) {
      continue;
    }
    if (Array.isArray(value)) {
      if (value.length > 0) {
        pairs.push([
          humanise(key),
          value.map((item) => (typeof item === "object" ? JSON.stringify(item) : String(item))).join(", "),
        ]);
      }
    } else if (value !== "") {
      pairs.push([humanise(key), String(value)]);
    }
  }
  if (filters.sort) {
    pairs.push(["Sort", `${filters.sort} ${filters.order ?? "asc"}`]);
  }
  return pairs;
};

// Writes the page of results on screen with the fields picked: the rows
// shown, at the request's offset among the `total` the search matches
export const exportSearchResults = (
  entityType: EntityTypePlural,
  filters: SearchFilters,
  rows: readonly SearchRow[],
  total: number,
  format: TableFormat,
  keys: readonly string[],
): Promise<void> =>
  writeTableExport(format, () => ({
    tables: [tableOfSearchResults(entityType, keys, [...rows])],
    meta: {
      title: typeLabel(entityType),
      generatedAt: new Date(),
      source: SOURCE,
      filters: searchExportFilters(filters),
      note: pageNote(filters.offset ?? 0, rows.length, Math.max(total, rows.length)),
    },
    filename: ["search", entityType, filters.q],
  }));
